import { apiClient } from '@/services/apiClient';

// ── Types ────────────────────────────────────────────────────────────────────

export interface FormSummary {
  formGuid: string;
  formTitle: string;
  formDescription?: string;
  isActive: boolean;
  effStartDt?: string;
  effEndDt?: string;
  sectionCount: number;
  fieldCount: number;
  submissionCount: number;
}

export interface FormManagementDetail {
  formGuid: string;
  formTitle: string;
  formDescription?: string;
  formCompleteMessage?: string;
  formFooter?: string;
  isActive: boolean;
  effStartDt?: string;
  effEndDt?: string;
  sections: FormSectionManagement[];
}

export interface FormSectionManagement {
  formSectionGuid: string;
  title?: string;
  description?: string;
  sortOrder: number;
  sectionType: string;
  formSectionConfig?: string;
  targetNext?: string;
  targetPrev?: string;
  buttonNext?: string;
  buttonPrev?: string;
  isActive: boolean;
  fields: FormFieldManagement[];
}

export interface FormFieldManagement {
  formFieldGuid: string;
  formSectionGuid?: string;
  title?: string;
  subtitle?: string;
  sortOrder: number;
  required: number;
  formFieldConfig?: string;
  conditionTarget?: string;
  condition?: string;
  isActive: boolean;
  options: FormFieldOptionManagement[];
}

export interface FormFieldOptionManagement {
  formFieldOptionGuid?: string;
  formFieldOption: string;
  fieldType?: string;
}

export interface FormSubmissionSummary {
  formSubmittedGuid?: string;
  createUser: string;
  createDt: string;
  formSubmittedStatus: string;
  submittedFirstName?: string;
  submittedLastName?: string;
  submittedEmail?: string;
  submittedOrgStructure?: string;
  submittedOrgStructureDept?: string;
}

// ── Request types ────────────────────────────────────────────────────────────

export interface CreateFormRequest {
  formTitle: string;
  formDescription?: string;
  formCompleteMessage?: string;
  formFooter?: string;
}

export interface UpdateFormBasicInfoRequest {
  formTitle: string;
  formDescription?: string;
  formCompleteMessage?: string;
  formFooter?: string;
}

export interface UpsertFormSectionRequest {
  formSectionGuid?: string;
  title?: string;
  description?: string;
  sortOrder: number;
  sectionType: string;
  formSectionConfig?: string;
  targetNext?: string;
  targetPrev?: string;
  buttonNext?: string;
  buttonPrev?: string;
}

export interface UpsertFormFieldRequest {
  formFieldGuid?: string;
  formSectionGuid?: string;
  title?: string;
  subtitle?: string;
  sortOrder: number;
  required: number;
  formFieldConfig?: string;
  conditionTarget?: string;
  condition?: string;
}

export interface UpsertFormFieldOptionRequest {
  formFieldOptionGuid?: string;
  formFieldOption: string;
  fieldType?: string;
}

// ── API functions ────────────────────────────────────────────────────────────

const BASE = '/api/forms/manage';

export async function getForms(includeInactive = false): Promise<FormSummary[]> {
  const res = await apiClient.get<FormSummary[]>(`${BASE}?includeInactive=${includeInactive}`);
  return res.data;
}

export async function getFormByGuid(guid: string): Promise<FormManagementDetail> {
  const res = await apiClient.get<FormManagementDetail>(`${BASE}/${guid}`);
  return res.data;
}

export async function createForm(req: CreateFormRequest): Promise<{ formGuid: string }> {
  const res = await apiClient.post<{ formGuid: string }>(BASE, req);
  return res.data;
}

export async function updateFormBasicInfo(guid: string, req: UpdateFormBasicInfoRequest): Promise<void> {
  await apiClient.patch(`${BASE}/${guid}/basic-info`, req);
}

export async function deleteForm(guid: string): Promise<void> {
  await apiClient.delete(`${BASE}/${guid}`);
}

export async function upsertSection(formGuid: string, req: UpsertFormSectionRequest): Promise<{ formSectionGuid: string }> {
  const res = await apiClient.post<{ formSectionGuid: string }>(`${BASE}/${formGuid}/sections`, req);
  return res.data;
}

export async function deleteSection(formGuid: string, sectionGuid: string): Promise<void> {
  await apiClient.delete(`${BASE}/${formGuid}/sections/${sectionGuid}`);
}

export async function upsertField(formGuid: string, req: UpsertFormFieldRequest): Promise<{ formFieldGuid: string }> {
  const res = await apiClient.post<{ formFieldGuid: string }>(`${BASE}/${formGuid}/fields`, req);
  return res.data;
}

export async function deleteField(formGuid: string, fieldGuid: string): Promise<void> {
  await apiClient.delete(`${BASE}/${formGuid}/fields/${fieldGuid}`);
}

export async function upsertOption(formGuid: string, fieldGuid: string, req: UpsertFormFieldOptionRequest): Promise<{ formFieldOptionGuid: string }> {
  const res = await apiClient.post<{ formFieldOptionGuid: string }>(`${BASE}/${formGuid}/fields/${fieldGuid}/options`, req);
  return res.data;
}

export async function deleteOption(formGuid: string, fieldGuid: string, optionGuid: string): Promise<void> {
  await apiClient.delete(`${BASE}/${formGuid}/fields/${fieldGuid}/options/${optionGuid}`);
}

export async function getFormSubmissions(formGuid: string): Promise<FormSubmissionSummary[]> {
  const res = await apiClient.get<FormSubmissionSummary[]>(`${BASE}/${formGuid}/submissions`);
  return res.data;
}
